// skillgenpro-frontend/src/error-framework/safeData.js
//
// Defensive accessors for data that crossed a network boundary.
//
// The crash class these exist for is the single most common one in this
// codebase: a component does `items.map(...)` where `items` was set from
// `res.data.data`. On the happy path that is an array. When the endpoint
// returns `{success:true}` with no `data`, or wraps the list one level
// deeper, or the DB hands back a JSON *string* for a column, `items` is
// undefined / an object / a string — and `.map is not a function` unmounts
// the whole route.
//
// These helpers are deliberately opt-in and non-magical: they never mutate,
// never throw, and always return the shape the caller asked for. Swapping
// `foo.map(fn)` for `safeArray(foo).map(fn)` cannot change behaviour on the
// happy path — it only removes the crash on the unhappy one.

/**
 * Always returns a real array.
 *   safeArray(undefined)        -> []
 *   safeArray(null)             -> []
 *   safeArray('abc')            -> []   (a string has .length but no .map)
 *   safeArray({a:1})            -> []
 *   safeArray([1,2])            -> [1,2] (same reference)
 */
export const safeArray = (value) => (Array.isArray(value) ? value : []);

/**
 * Pull a list out of an API envelope without caring which shape came back.
 * Tries, in order: the value itself, `.data`, `.items`, `.results`, `.rows`,
 * and `.records` — covering every wrapper this backend currently uses.
 */
export const safeList = (payload, key) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  if (key && Array.isArray(payload[key])) return payload[key];
  const candidates = ['data', 'items', 'results', 'rows', 'records', 'list'];
  for (let i = 0; i < candidates.length; i += 1) {
    const found = payload[candidates[i]];
    if (Array.isArray(found)) return found;
  }
  // One level deeper: { data: { items: [...] } }
  const inner = payload.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    for (let i = 0; i < candidates.length; i += 1) {
      const found = inner[candidates[i]];
      if (Array.isArray(found)) return found;
    }
  }
  return [];
};

/** Always returns a plain object — safe for Object.keys/values/entries. */
export const safeObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

export const safeKeys = (value) => Object.keys(safeObject(value));
export const safeValues = (value) => Object.values(safeObject(value));
export const safeEntries = (value) => Object.entries(safeObject(value));

/** Length of anything, without throwing on null/undefined. */
export const safeLength = (value) => {
  if (Array.isArray(value) || typeof value === 'string') return value.length;
  return 0;
};

export const safeString = (value, fallback) => {
  if (typeof value === 'string') return value;
  if (value == null) return fallback != null ? fallback : '';
  try {
    return String(value);
  } catch (e) {
    return fallback != null ? fallback : '';
  }
};

export const safeNumber = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : (fallback != null ? fallback : 0);
};

/**
 * JSON.parse that cannot throw. Corrupt localStorage is a real, observed
 * failure mode here (a truncated write leaves a value that is present but
 * unparseable), and an uncaught SyntaxError inside a mount-time effect
 * takes the whole route down.
 */
export const safeJsonParse = (raw, fallback) => {
  if (raw == null || raw === '') return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed == null ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
};

/** Read + parse a localStorage key without ever throwing. */
export const safeLocalStorageJson = (key, fallback) => {
  try {
    return safeJsonParse(localStorage.getItem(key), fallback);
  } catch (e) {
    // Access itself can throw in private-mode / disabled-storage browsers.
    return fallback;
  }
};

/** Optional-chaining style deep read: safeGet(obj, 'a.b.c', 'default'). */
export const safeGet = (source, path, fallback) => {
  try {
    const parts = String(path).split('.');
    let cur = source;
    for (let i = 0; i < parts.length; i += 1) {
      if (cur == null) return fallback;
      cur = cur[parts[i]];
    }
    return cur === undefined ? fallback : cur;
  } catch (e) {
    return fallback;
  }
};

export default {
  safeArray, safeList, safeObject, safeKeys, safeValues, safeEntries,
  safeLength, safeString, safeNumber, safeJsonParse, safeLocalStorageJson, safeGet,
};
